<script setup lang="ts">
	import type { ColumnType } from 'ant-design-vue/lib/table'
	import useWalletApi from '~/services/apis/wallet'
	import useTransactionsApi from '@/services/apis/transactions.js'

	defineProps<{
		tableColumns: ColumnType[]
	}>()
	const { fetchWalletDetails } = useWalletApi()
	const walletTabs = ref<lineTabContext[]>([
		{
			name: 'All Transactions',
			key: 'all'
		},
		{
			name: 'Paid',
			key: 'inflow'
		},
		{
			name: 'Withdrawn',
			key: 'outflow'
		}
	])

	const filterBy = shallowRef<'inflow' | 'outflow' | 'all'>('all')
	const { currencyToNaira } = useCurrency()
	const { GetTransactions } = useTransactionsApi()
	const { formatDate } = useCustomDate()
	const tableOptions = computed<TableFilterOptions>(() => {
		return {
			filter: { type: filterBy.value !== 'all' ? filterBy.value || '' : '', source: 'wallet' }
		}
	})
	const getAllTransaction = (arg: unknown) => {
		return resolve(async () => {
			const result = await GetTransactions(arg)
			if (result?.data) {
				return result.data
			}
			return null
		})
	}

	onMounted(() => {
		fetchWalletDetails()
	})
</script>

<template>
	<section class="md:pt-24">
		<shared-header
			title="Transactions"
			sub-title="View your wallet transactions"
			sub-title-class="font-normal"
			class="mb-10"
		/>
		<div>
			<tabs-line-tab
				v-model:active-tab="filterBy"
				btn-class="md:text-lg"
				:tabs="walletTabs"
				:ui="{
					tab: {
						active: {
							color: 'text-blue-950'
						}
					},
					line: {
						borderColor: 'border-blue-950'
					}
				}"
				tab-body-class="mt-6"
			>
				<div class="overflow-x-auto whitespace-pre">
					<ui-data-table :columns="tableColumns" :records="getAllTransaction" :options="tableOptions">
						<template #bodyCell="{ column, record }">
							<div v-if="column?.key === 'name'" class="flex items-center space-x-3">
								<div
									class="flex h-[35px] w-[35px] flex-none items-center justify-center rounded-full"
									:class="{
										'bg-error-50': record?.status === 'failed' || record?.type == 'outflow',
										'bg-success-50': record?.type === 'inflow'
									}"
								>
									<lazy-tools-icon
										dir="arrows"
										:icon="record?.type === 'inflow' ? 'inflow' : 'outflow'"
									/>
								</div>
								<span>
									{{ record?.name }}
								</span>
							</div>
							<div v-if="column?.key === 'transaction_type'" class="flex items-center space-x-3">
								<div>
									<lazy-tools-icon v-if="record?.type === 'outflow'" dir="arrows" icon="outflow" />
									<lazy-tools-icon v-if="record?.type === 'inflow'" dir="arrows" icon="inflow" />
								</div>
								<div class="mt-3 flex items-center space-x-3">
									<p class="">
										{{ record?.name }}
									</p>
									<p
										v-if="record?.isWithdrawable"
										class="inline-flex rounded-full bg-purple-100 px-3 py-1.5 text-[10px] font-medium text-purple-500"
									>
										Withdraw-able
									</p>
								</div>
							</div>

							<shared-badge
								v-if="column?.key === 'status'"
								:text="record?.status"
								:pending="record?.status === 'pending'"
								:active="record?.status === 'successful'"
								:failed="record?.status === 'failed'"
							/>
							<span v-if="column?.key === 'amount'">
								{{ currencyToNaira(record?.amount || 0) }}
							</span>

							<span v-if="column?.key === 'date'">
								{{ formatDate(record?.created_at ?? '', 'DD MMM YYYY') }}
							</span>
							<nuxt-link
								v-if="column?.key === 'details'"
								:to="`/transaction-history/${record?.transaction_id}`"
								class="text-primary-2"
							>
								View
							</nuxt-link>
						</template>
					</ui-data-table>
				</div>
			</tabs-line-tab>
		</div>
	</section>
</template>
